import React, { useEffect, useState } from "react";
import { StepWelcome } from "./StepWelcome";
import { StepYourDetails } from "./StepYourDetails";
import { StepEdition } from "./StepEdition";
import { AppItem, StepApps } from "./StepApps";
import { StepSummary } from "./StepSummary";
import { StepComplete } from "./StepComplete";
import { IDropdownOption } from "@fluentui/react";
import { IEntity } from "../../types";
import { AccountRegService, AvailableAppService, CustomerCountryService, CustomerSectorService, IEntityService } from "../../services";
import { EditionType } from "../../AppGlobals";
import { StepVerifyEmail } from "./StepVerifyEmail";
import { StepAccountSelection } from "./StepAccountSelection";
import { StepCreateAccountRef } from "./StepCreateAccountRef";

interface Props {
    onStepChange?: (step: number, maxStep: number) => void;
}

export interface YourDetailsState {
    organisationName: string;
    address: string;
    country?: number;
    sector?: number;
    numberOfStaff: string;
    website: string;

    title?: string;
    firstname: string;
    surname: string;
    email: string;
    phone: string;

    CustomerRef?: string;
    UsedExistingAccount?: boolean;
}

const titleOptions: IDropdownOption[] = [
    { key: "Mr", text: "Mr" },
    { key: "Mrs", text: "Mrs" },
    { key: "Ms", text: "Ms" },
    { key: "Dr", text: "Dr" }
];

export const CreateAccountWizard: React.FC<Props> = ({ onStepChange }) => {
    const customerCountryService = new CustomerCountryService();
    const customerSectorService = new CustomerSectorService();
    const availableAppService = new AvailableAppService();
    const accountRegService = new AccountRegService();

    const ZEDX_IMAGES_ROOT = process.env.REACT_APP_ZedXImagesRoot
    const [accountRegId, setAccountRegId] = useState<number>(0);
    const [hasExistingAccounts, setHasExistingAccounts] = useState<boolean | null>(null);

    const [apps, setApps] = useState<AppItem[]>([]);

    const [customerCountries, setCustomerCountries] = useState<IEntity[]>([]);
    const [customerSectors, setCustomerSectors] = useState<IEntity[]>([]);

    const [edition, setEdition] = useState<EditionType | null>(null);

    const [selectedApps, setSelectedApps] = useState<number[]>([]);

    const [details, setDetails] = useState<YourDetailsState>({
        organisationName: "",
        address: "",
        country: undefined,
        sector: undefined,
        numberOfStaff: "",
        website: "",
        title: undefined,
        firstname: "",
        surname: "",
        email: "",
        phone: ""
    });

    const saveProgress = async (nextStep: number, overrides?: Partial<YourDetailsState>) => {
        try {
            const model = {
                ID: accountRegId,
                Title: '',
                CustomerRef: overrides?.CustomerRef ?? details.CustomerRef,
                UsedExistingAccount: overrides?.UsedExistingAccount ?? details.UsedExistingAccount,

                // Step 2 (Details)
                OrganisationName: details.organisationName,
                Address: details.address,
                CustomerCountryID: details.country,
                CustomerSectorID: details.sector,
                NumberOfStaff: details.numberOfStaff,
                Website: details.website,
                UserTitle: details.title,
                Firstname: details.firstname,
                Surname: details.surname,
                Email: details.email,
                Phone: details.phone,

                // Step 3
                EditionID: edition,

                // Step 4
                AppIDs: selectedApps.join(","),

                // Progress tracking
                RegStep: `Step${nextStep}`,
                RegCompleted: nextStep === 9,

            };

            const result = await accountRegService.save(model);

            // IMPORTANT: store ID after first save
            if (!accountRegId && result?.ID) {
                setAccountRegId(result.ID);
            }

            //console.log("Saved", result);
        } catch (err) {
            console.error("Save failed", err);
        }
    };

    // const goToStep = (nextStep: number) => {

    //     setStep(nextStep);
    //     if (nextStep > maxStep) {
    //         setMaxStep(nextStep);
    //     }

    //     if (onStepChange) {
    //         onStepChange(nextStep, maxStep);
    //     }
    // };

    const goToStep = async (nextStep: number) => {

        // Save BEFORE moving forward (but not on step 1)
        if (step > 1) {
            await saveProgress(nextStep);
        }

        setStep(nextStep);

        if (nextStep > maxStep) {
            setMaxStep(nextStep);
        }

        if (onStepChange) {
            onStepChange(nextStep, maxStep);
        }
    };

    const [step, setStep] = useState<number>(1);
    const [maxStep, setMaxStep] = useState<number>(1);


    useEffect(() => {
        const content = document.querySelector(".content") as HTMLElement;
        const main = document.querySelector(".main") as HTMLElement;

        let container: HTMLElement | null = null;

        if (content && content.scrollHeight > content.clientHeight) {
            container = content;
        } else if (main && main.scrollHeight > main.clientHeight) {
            container = main;
        }

        if (container) {
            container.scrollTo({
                top: 0,
                behavior: "smooth"
            });
        }
    }, [step]);

    useEffect(() => {

        const loadCountries = async () => {
            try {
                const data = await customerCountryService.readAll();
                setCustomerCountries(data);
                //console.log("Countries loaded", data);
            } catch (err) {
                console.error("Countries failed", err);
            }
        };

        const loadSectors = async () => {
            try {
                const data = await customerSectorService.readAll();
                setCustomerSectors(data);
                //console.log("Sectors loaded", data);
            } catch (err) {
                console.error("Sectors failed", err);
            }
        };

        loadCountries();
        loadSectors();

    }, []);

    // Load apps whenever edition, country, or sector changes
    useEffect(() => {
        const loadApps = async () => {
            if (!edition || !details.country || !details.sector) return;

            const systemCountryID = customerCountries.find(c => c.ID === details.country)?.['SystemCountryID'];
            const systemSectorID = customerSectors.find(s => s.ID === details.sector)?.['SystemSectorID'];

            try {
                const apiApps = await availableAppService.readAvailableAppsWithFilters(
                    edition,         // EditionType enum value
                    systemSectorID,  // systemSectorID
                    systemCountryID  // systemCountryID
                );

                // Map API data to AppItem for StepApps
                const mappedApps: AppItem[] = apiApps.map(a => ({
                    id: a.ID,
                    title: a.Title,
                    image: `${ZEDX_IMAGES_ROOT}${a['Image']}`
                }));

                setApps(mappedApps);
                //console.log("Apps loaded", mappedApps);

                // Optionally reset selected apps when edition/sector/country changes
                setSelectedApps([]);
            } catch (err) {
                console.error("Apps failed", err);
            }
        };

        loadApps();
    }, [edition, details.country, details.sector]);

    const handleAfterVerification = async () => {
        try {
            const accounts = await accountRegService.getByEmail(details.email, accountRegId);

            if (accounts.length > 0) {
                setHasExistingAccounts(true);
                goToStep(7);
            } else {
                setHasExistingAccounts(false);
                goToStep(8); // skip Step 7
            }
        } catch (err) {
            console.error("Account check failed", err);
        }
    };


    return (
        <div className="create-account-wizard">

            {step === 1 && (
                <StepWelcome
                    onContinue={() => goToStep(2)}
                />
            )}

            {step === 2 && (
                <StepYourDetails
                    details={details}
                    setDetails={setDetails}
                    onContinue={() => goToStep(3)}
                    onBack={() => goToStep(1)}
                    customerCountries={customerCountries}
                    customerSectors={customerSectors}
                    titleOptions={titleOptions}
                />
            )}

            {step === 3 && (
                <StepEdition
                    selectedEdition={edition}
                    setSelectedEdition={setEdition}
                    onContinue={() => goToStep(4)}
                    onBack={() => goToStep(2)}
                />
            )}

            {step === 4 && (
                <StepApps
                    apps={apps}
                    selectedApps={selectedApps}
                    setSelectedApps={setSelectedApps}
                    onContinue={() => goToStep(5)}
                    onBack={() => goToStep(3)}
                />
            )}

            {step === 5 && (
                <StepSummary
                    details={details}
                    edition={edition}
                    apps={selectedApps}
                    customerCountries={customerCountries}
                    customerSectors={customerSectors}
                    allApps={apps}
                    onContinue={() => goToStep(6)}
                    onBack={() => goToStep(4)}
                />
            )}

            {step === 6 && (
                <StepVerifyEmail
                    accountRegId={accountRegId}
                    email={details.email}
                    //onVerified={() => goToStep(7)}
                    onVerified={handleAfterVerification}
                    onBack={() => goToStep(5)}
                />
            )}

            {step === 7 && hasExistingAccounts && (
                <StepAccountSelection
                    email={details.email}
                    accountRegId={accountRegId}
                    onContinue={async (useExisting) => {

                        if (useExisting) {
                            setDetails(prev => ({ ...prev, UsedExistingAccount: true }));
                            await saveProgress(9, { UsedExistingAccount: true });
                            setStep(9);
                        } else {
                            goToStep(8);
                        }
                    }}
                />
            )}

            {step === 8 && (
                <StepCreateAccountRef
                    accountRegId={accountRegId}
                    companyName={details.organisationName}
                    onCreated={async (customerRef) => {
                        setDetails(prev => ({ ...prev, CustomerRef: customerRef }));
                        await saveProgress(9, { CustomerRef: customerRef });
                        setStep(9);
                    }}
                    onBack={() => goToStep(7)}
                />
            )}

            {step === 9 && (
                <StepComplete
                    usedExistingAccount={details.UsedExistingAccount}
                    customerRef={details.CustomerRef}
                />
            )}

        </div>
    );
};